import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AdminShell from "@/components/admin/AdminShell";
import { DOMAIN_CONFIG } from "@/lib/domainConfig";
import { Eye, MousePointerClick, TrendingUp, Users, Loader2, RefreshCw } from "lucide-react";

const RANGES = [
  { key: "7d", label: "7 Tage", days: 7 },
  { key: "30d", label: "30 Tage", days: 30 },
  { key: "90d", label: "90 Tage", days: 90 },
  { key: "180d", label: "6 Monate", days: 180 },
  { key: "365d", label: "12 Monate", days: 365 },
];

const fmtPct = (n) => `${(n * 100).toFixed(1)}%`;
const fmtInt = (n) => n.toLocaleString("de-DE");

const brandLabel = (host) =>
  DOMAIN_CONFIG[host]?.brandName || (host === "unknown" ? "Unbekannt" : host);

const KpiCard = ({ icon: Icon, label, value, subtle }) => (
  <div className="rounded-3xl bg-white border border-neutral-200 p-6">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-2xl bg-neutral-100 grid place-items-center text-neutral-700">
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">{label}</div>
    </div>
    <div className="mt-4 font-display text-4xl font-light tabular-nums text-neutral-950">
      {value}
    </div>
    {subtle && <div className="mt-1 text-xs text-neutral-500">{subtle}</div>}
  </div>
);

const AdminAnalytics = () => {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (rangeKey) => {
    setLoading(true);
    try {
      const days = RANGES.find((r) => r.key === rangeKey)?.days || 30;
      const { data: payload } = await api.get(`/admin/analytics?days=${days}`);
      setData(payload);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(range);
  }, [range]);

  const max = useMemo(() => {
    if (!data?.per_domain?.length) return 1;
    return Math.max(1, ...data.per_domain.map((d) => d.views));
  }, [data]);

  const rangeLabel = RANGES.find((r) => r.key === range)?.label || "";

  return (
    <AdminShell
      title="Analytics"
      subtitle={`Besucher · Sessions · Conversions pro Domain · Zeitraum „${rangeLabel}"`}
      actions={
        <button
          onClick={() => load(range)}
          data-testid="analytics-refresh"
          className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm hover:bg-neutral-50"
        >
          <RefreshCw className="w-4 h-4" />
          Aktualisieren
        </button>
      }
    >
      {/* Range pills */}
      <div className="mb-6 inline-flex flex-wrap items-center gap-1 rounded-full bg-neutral-100 p-1">
        {RANGES.map((r) => {
          const active = r.key === range;
          return (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`analytics-range-${r.key}`}
              data-active={active}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                active ? "bg-neutral-950 text-white shadow-sm" : "text-neutral-600 hover:text-neutral-900"
              }`}
            >
              {r.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="rounded-3xl bg-white border border-neutral-200 p-16 grid place-items-center text-neutral-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : !data ? (
        <div className="rounded-3xl bg-white border border-neutral-200 p-12 text-center text-neutral-500">
          Keine Daten verfügbar.
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon={Eye}
              label="Seitenaufrufe"
              value={fmtInt(data.totals.views)}
            />
            <KpiCard
              icon={Users}
              label="Sessions"
              value={fmtInt(data.totals.sessions)}
              subtle="Eindeutige Besucher (Browser-Sessions)"
            />
            <KpiCard
              icon={MousePointerClick}
              label="Leads"
              value={fmtInt(data.totals.leads)}
            />
            <KpiCard
              icon={TrendingUp}
              label="Conversion-Rate"
              value={fmtPct(data.totals.conversion_rate)}
              subtle="Leads / Sessions"
            />
          </div>

          {/* Per-domain breakdown */}
          <div className="mt-8 rounded-3xl bg-white border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-display text-lg font-medium">Performance pro Domain</h2>
                <div className="text-xs text-neutral-500 mt-0.5">
                  Welche Marke bringt die meisten Besucher und Leads?
                </div>
              </div>
            </div>
            {data.per_domain.length === 0 ? (
              <div
                className="text-sm text-neutral-500 py-8 text-center"
                data-testid="analytics-empty"
              >
                Noch keine Tracking-Daten. Besuche zur Hauptseite werden ab sofort gezählt.
              </div>
            ) : (
              <ul className="space-y-5">
                {data.per_domain.map((row) => {
                  const widthPct = Math.max(2, (row.views / max) * 100);
                  return (
                    <li
                      key={row.domain}
                      data-testid={`analytics-domain-row-${row.domain.replace(/\./g, "-")}`}
                      className="space-y-1.5"
                    >
                      <div className="flex items-center justify-between text-sm flex-wrap gap-2">
                        <div className="min-w-0">
                          <span className="font-medium text-neutral-900">{brandLabel(row.domain)}</span>
                          <span className="text-xs text-neutral-400 ml-2">{row.domain}</span>
                        </div>
                        <div className="flex items-center gap-5 shrink-0 text-xs text-neutral-500 tabular-nums">
                          <span>
                            <span className="font-semibold text-neutral-900">{fmtInt(row.views)}</span>{" "}
                            Views
                          </span>
                          <span>
                            <span className="font-semibold text-neutral-900">{fmtInt(row.sessions)}</span>{" "}
                            Sessions
                          </span>
                          <span>
                            <span className="font-semibold text-neutral-900">{fmtInt(row.leads)}</span>{" "}
                            Leads
                          </span>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${
                              row.conversion_rate >= 0.05
                                ? "bg-emerald-50 text-emerald-700"
                                : row.conversion_rate > 0
                                ? "bg-sky-50 text-sky-700"
                                : "bg-neutral-100 text-neutral-500"
                            }`}
                          >
                            {fmtPct(row.conversion_rate)} CR
                          </span>
                        </div>
                      </div>
                      <div className="h-2 w-full rounded-full bg-neutral-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-sky-500 to-sky-700 transition-[width] duration-500"
                          style={{ width: `${widthPct}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Top paths */}
          <div className="mt-8 rounded-3xl bg-white border border-neutral-200 p-6">
            <h2 className="font-display text-lg font-medium">Top Seiten</h2>
            <div className="text-xs text-neutral-500 mt-0.5 mb-5">
              Die 10 meistbesuchten URLs über alle Domains im gewählten Zeitraum
            </div>
            {data.top_paths.length === 0 ? (
              <div className="text-sm text-neutral-500 py-4">Noch keine Seitenaufrufe.</div>
            ) : (
              <ol className="space-y-2.5" data-testid="analytics-top-paths">
                {data.top_paths.map((p, i) => (
                  <li
                    key={p.path}
                    className="flex items-center gap-4 text-sm"
                  >
                    <span className="w-6 text-neutral-400 tabular-nums text-xs text-right">
                      {i + 1}.
                    </span>
                    <span className="flex-1 min-w-0 truncate font-mono text-[13px] text-neutral-700">
                      {p.path}
                    </span>
                    <span className="text-neutral-900 font-semibold tabular-nums shrink-0">
                      {fmtInt(p.views)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="mt-8 text-xs text-neutral-400">
            Tracking ist DSGVO-freundlich: keine IP-Adresse, kein User-Agent, kein Fingerprinting. Nur anonyme Session-IDs im localStorage des Browsers.
          </div>
        </>
      )}
    </AdminShell>
  );
};

export default AdminAnalytics;
